import React from 'react';
import PropTypes from 'prop-types';
import { TableCell, TableRow } from '@material-ui/core';
import classnames from 'classnames';
import { TableHeaderCell } from 'core';
import { dateFormatter } from 'core/helpers/formatters';
import { ElemType, MapClassesToElem } from 'core/helpers/styles-helpers';
import Checkbox from 'core/components/checkbox';
import { IncidentStatusTag, checkboxLabel } from 'common/components/ignore-incidents';

import { getIncidentTypeLabel } from './helpers';

// staff-keyed incidents carry addedBy (stamped by rater-helpers addIncidents);
// everything else came in through vendor report pulls
const getSourceLabel = (incident, isAutoPolicy) => {
  if (incident.addedBy) {
    return `Manual (${incident.addedBy})`;
  }
  return isAutoPolicy ? incident.source : 'Reports';
};

const getCategoryLabel = (incident) => {
  if (incident.category !== 'claim') {
    return 'Violation';
  }
  return incident.notRated ? 'Claim - Not rated (older than 3 years)' : 'Claim';
};

export const IncidentTableHeader = ({ isAutoPolicy, classes, onSort, canSelect }) => {
  return (
    <>
      {canSelect && <TableCell className={classnames(classes.headerCell, classes.tableHeader)}>SELECT</TableCell>}
      <TableHeaderCell
        key={'date'}
        columnId={'date'}
        onSort={onSort}
        className={classes.tableSortLabelRoot}
        tableClasses={classes.headerCell}
      >
        Date
      </TableHeaderCell>
      {isAutoPolicy && <TableCell className={classes.headerCell}>DRIVER</TableCell>}
      <TableCell className={classes.headerCell}>TYPE</TableCell>
      <TableCell className={classes.headerCell}>CATEGORY</TableCell>
      <TableCell className={classes.headerCell}>SOURCE</TableCell>
      {isAutoPolicy && <TableCell className={classes.headerCell}>STATUS</TableCell>}
    </>
  );
};

IncidentTableHeader.propTypes = {
  classes: PropTypes.object.isRequired,
  isAutoPolicy: PropTypes.bool.isRequired,
  onSort: PropTypes.func.isRequired,
  canSelect: PropTypes.bool.isRequired
};

// Auto incidents are grouped under a header row per date (#36871): only the most severe incident on a
// date counts, so the status of each one reads against the others on its date.
const groupByDate = (incidents) =>
  incidents.reduce((groups, incident) => {
    const last = groups[groups.length - 1];
    if (last?.date === incident.date) {
      last.incidents.push(incident);
    } else {
      groups.push({ date: incident.date, incidents: [incident] });
    }
    return groups;
  }, []);

const IncidentTableRows = ({
  incidents,
  classes,
  isAutoPolicy,
  onSelect,
  selectedIncidents,
  canSelect,
  isSelectable
}) => {
  const cellClasses = MapClassesToElem(`dark-${ElemType.TableCell}`, classes);
  const columnCount = (canSelect ? 1 : 0) + (isAutoPolicy ? 6 : 4);

  const renderRow = (incident) => (
    <TableRow key={incident.id} hover classes={MapClassesToElem(`dark-${ElemType.TableRow}`, classes)}>
      {canSelect && (
        <TableCell classes={cellClasses}>
          {isSelectable(incident) && (
            <Checkbox
              mode="dark"
              id={`checkbox-${incident.id}`}
              label={<span className={classes.srOnly}>{checkboxLabel(incident)}</span>}
              onChange={({ target: { checked } }) => onSelect(incident, checked)}
              value={incident.id in selectedIncidents}
            />
          )}
        </TableCell>
      )}
      <TableCell classes={cellClasses}>{isAutoPolicy ? null : dateFormatter(incident.date)}</TableCell>
      {isAutoPolicy && <TableCell classes={cellClasses}>{incident.driver}</TableCell>}
      <TableCell classes={cellClasses} className={classes.noWrap}>
        {getIncidentTypeLabel(incident.type)}
      </TableCell>
      <TableCell classes={cellClasses}>{getCategoryLabel(incident)}</TableCell>
      <TableCell classes={cellClasses}>
        {getSourceLabel(incident, isAutoPolicy)}
        {incident.isCourtRecord ? ' (Court Record)' : ''}
      </TableCell>
      {isAutoPolicy && (
        <TableCell classes={cellClasses}>{incident.status && <IncidentStatusTag incident={incident} />}</TableCell>
      )}
    </TableRow>
  );

  if (!isAutoPolicy) {
    return <>{incidents.map(renderRow)}</>;
  }

  return (
    <>
      {groupByDate(incidents).map(({ date, incidents: incidentsOnDate }) => (
        <React.Fragment key={date}>
          <TableRow className={classes.dateGroupRow}>
            <TableCell component="th" scope="rowgroup" colSpan={columnCount} className={classes.dateGroupHeader}>
              {dateFormatter(date)}
            </TableCell>
          </TableRow>
          {incidentsOnDate.map(renderRow)}
        </React.Fragment>
      ))}
    </>
  );
};

IncidentTableRows.propTypes = {
  classes: PropTypes.object.isRequired,
  incidents: PropTypes.array.isRequired,
  selectedIncidents: PropTypes.object,
  isAutoPolicy: PropTypes.bool.isRequired,
  onSelect: PropTypes.func,
  canSelect: PropTypes.bool.isRequired,
  isSelectable: PropTypes.func
};

IncidentTableRows.defaultProps = {
  selectedIncidents: {},
  onSelect: () => {},
  // not-rated claims aren't in the rating data, so there is nothing to remove or ignore
  isSelectable: (incident) => !incident.notRated
};

export default IncidentTableRows;
